export type ImportResultPayload = {
  count?: number
  processed?: number
  successful?: number
  errors?: { rowNumber: number; message: string }[]
  data?: Record<string, unknown>[]
  status?: string
  message?: string
}

export type ImportOfficeTarget = {
  organizationId: number
  officeId: number
}

export type OfficeSelectOption = {
  officeId: number
  organizationId: number
  label: string
}

export function officeOptionKey(option: Pick<OfficeSelectOption, 'organizationId' | 'officeId'>) {
  return `${option.organizationId}:${option.officeId}`
}

function parseUploadResponse(xhr: XMLHttpRequest): ImportResultPayload {
  return JSON.parse(xhr.responseText)
}

function formatUploadError(response: ImportResultPayload, status: number): string {
  if (response?.message) return response.message
  const firstError = response?.errors?.[0]
  if (firstError?.message) return firstError.message
  return `Ошибка ${status}`
}

export function uploadOfficeUsers(
  file: File,
  officeId: number,
  organizationId: number,
  onProgress: (percent: number) => void,
): Promise<ImportResultPayload> {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('office_id', String(officeId))
    formData.append('organization_id', String(organizationId))

    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })
    xhr.addEventListener('load', () => {
      try {
        const response = parseUploadResponse(xhr)
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response)
        } else {
          reject(new Error(formatUploadError(response, xhr.status)))
        }
      } catch {
        reject(new Error('Некорректный ответ сервера'))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке')))
    xhr.open('POST', '/api/users_new/import')
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.send(formData)
  })
}

export type OrdersImportPersonItem = {
  name: string
  status: 'ok' | 'missing' | 'not_in_office'
  userId?: number
}

export type OrdersImportStageItem = {
  name: string
  status: 'ok' | 'missing'
  stageCode?: string
  suggestedStageCode?: string | null
  suggestedStageName?: string | null
}

export type OrdersImportAnalyzeData = {
  managers: OrdersImportPersonItem[]
  measurers: OrdersImportPersonItem[]
  stages: OrdersImportStageItem[]
  orderStages: Array<{ code: string; name: string }>
  officeUsers: Array<{ id: number; name: string | null; login: string | null }>
  rowHardErrors: Array<{ rowNumber: number; messages: string[] }>
  stats: {
    rowCount: number
    importableRowCount: number
    missingManagers: number
    missingMeasurers: number
    missingStages: number
  }
  needsWizard: boolean
}

export type OrdersImportPersonResolution = {
  name: string
  action: 'create' | 'map'
  userId?: number
}

export type OrdersImportStageResolution = {
  nameFromFile: string
  stageCode: string
}

export type OrdersImportResolutions = {
  managers?: OrdersImportPersonResolution[]
  measurers?: OrdersImportPersonResolution[]
  stages?: OrdersImportStageResolution[]
}

type AnalyzeResponse = ImportResultPayload & { data?: OrdersImportAnalyzeData }

function postOrdersImportForm(
  url: string,
  file: File,
  officeId: number,
  onProgress: (percent: number) => void,
  extraFields?: Record<string, string>,
): Promise<ImportResultPayload> {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('csvFile', file)
    formData.append('office_id', String(officeId))
    if (extraFields) {
      Object.entries(extraFields).forEach(([key, value]) => formData.append(key, value))
    }

    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })
    xhr.addEventListener('load', () => {
      try {
        const response = parseUploadResponse(xhr)
        if (xhr.status >= 200 && xhr.status < 300 && response.status !== 'error') {
          resolve(response)
        } else {
          reject(new Error(formatUploadError(response, xhr.status)))
        }
      } catch {
        reject(new Error('Некорректный ответ сервера'))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке')))
    xhr.open('POST', url)
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.send(formData)
  })
}

export function analyzeOrdersImport(
  file: File,
  officeId: number,
  onProgress: (percent: number) => void,
): Promise<OrdersImportAnalyzeData> {
  return postOrdersImportForm('/api/orders/import/analyze', file, officeId, onProgress).then(
    response => {
      const data = (response as AnalyzeResponse).data
      if (!data) {
        throw new Error('Не удалось проверить файл')
      }
      return data
    },
  )
}

export function commitOrdersImport(
  file: File,
  officeId: number,
  resolutions: OrdersImportResolutions,
  onProgress: (percent: number) => void,
): Promise<ImportResultPayload> {
  return postOrdersImportForm('/api/orders/import/commit', file, officeId, onProgress, {
    resolutions: JSON.stringify(resolutions || {}),
  })
}

/** Legacy direct import (без мастера). */
export function uploadOrdersImport(
  file: File,
  officeId: number,
  onProgress: (percent: number) => void,
): Promise<ImportResultPayload> {
  return postOrdersImportForm('/api/csv_upload', file, officeId, onProgress)
}

export const USERS_IMPORT_COLUMN_HINTS = [
  { name: 'ФИО', required: false, description: 'Отображаемое имя сотрудника' },
  { name: 'Телефон', required: false, description: 'Контактный телефон' },
  {
    name: 'Логин (e-mail)',
    required: true,
    description: 'Уникальный логин пользователя (обычно e-mail)',
  },
  { name: 'Пароль', required: true, description: 'Временный пароль для первого входа' },
]

export const ORDERS_IMPORT_COLUMN_HINTS = [
  { name: 'Дата создания обращения', required: true, description: 'Формат DD.MM.YYYY' },
  {
    name: 'Номер обращения',
    required: false,
    description: 'Информационное поле (не используется)',
  },
  {
    name: 'Стадия',
    required: true,
    description: 'Название стадии; если не совпадёт — мастер предложит сопоставить',
  },
  { name: 'Причина отказа', required: false, description: 'Если стадия связана с отменой' },
  { name: 'Менеджер', required: true, description: 'ФИО менеджера (если нет в системе — мастер предложит создать)' },
  { name: 'Вид контакта', required: true, description: 'Источник обращения (enum)' },
  { name: 'Контактное лицо клиента', required: false, description: 'ФИО клиента' },
  { name: 'Адрес', required: false, description: 'Адрес объекта' },
  { name: 'Телефон клиента', required: true, description: '11 цифр, начинается с 7 или 8' },
  { name: 'Дата замера', required: false, description: 'DD.MM.YYYY; вместе с «Отв за замер»' },
  { name: 'Отв за замер', required: false, description: 'ФИО замерщика' },
  { name: 'Сумма заказа', required: false, description: 'Число, например 93295 или 93295.50' },
  { name: 'Предоплата', required: false, description: 'Сумма оплаты' },
  { name: 'Дата оплаты', required: false, description: 'DD.MM.YYYY' },
  { name: 'Номер договора', required: false, description: 'Номер договора' },
  { name: 'Дата завершения заказа', required: false, description: 'DD.MM.YYYY' },
]
